<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'primevue/usetoast'
import CDashboardClassroomAttendance from '@/presentation/components/dashboard/CDashboardClassroomAttendance.vue'
import CDashboardClassroomModularization from '@/presentation/components/dashboard/CDashboardClassroomModularization.vue'
import CDashboardClassroomGeneralMetrics from '@/presentation/components/dashboard/CDashboardClassroomGeneralMetrics.vue'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom.ts'
import PSkeleton from 'primevue/skeleton'

// STORES
const dashboardClassroomStore = useDashboardClassroomStore()

// STORE REFS
const { dashboardSelectedClassroom, dashboardClassroomStoreError: classroomError } =
  storeToRefs(dashboardClassroomStore)

// LOCAL STATE
const localLoading = ref(true)
const localError = ref('')
const dataInitialized = ref(false)

// PROPS
const props = defineProps<{
  schoolId: number
  classroomId: number
}>()

// COMPOSABLES
const toast = useToast()

// COMPUTED
const hasError = computed(
  () => (classroomError.value && classroomError.value.hasError) || localError.value !== ''
)

const errorMessage = computed(
  () => localError.value || (classroomError.value?.hasError ? classroomError.value.message : '')
)

const isEmpty = computed(() => !localLoading.value && !dashboardSelectedClassroom.value)

// METHODS
/**
 * Charge les données de la classe
 */
const { setDashboardSelectedClassroomById } = dashboardClassroomStore

const loadClassroomData = async () => {
  // Si déjà initialisé, ne pas recharger
  if (dataInitialized.value) return

  try {
    localLoading.value = true
    localError.value = ''

    await setDashboardSelectedClassroomById(props.schoolId, props.classroomId)

    // Charger les assignments pour la classe une fois que la classe est sélectionnée
    await dashboardClassroomStore.fetchDashboardClassroomAssignments()

    // Marquer comme initialisé après le chargement réussi
    dataInitialized.value = true
  } catch (err) {
    localError.value =
      err instanceof Error ? err.message : 'Impossible de charger les détails du groupe'

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: localError.value,
      life: 5000
    })

    console.error('Erreur lors du chargement des données de classe:', err)
  } finally {
    localLoading.value = false
  }
}

// LIFECYCLE
onMounted(async () => {
  await Promise.all([
    loadClassroomData(),
    dashboardClassroomStore.fetchDashboardClassroomDiagnosticState()
  ])
})


// Observer les changements de props pour recharger les données si nécessaire
watch(
  [() => props.schoolId, () => props.classroomId],
  async ([newSchoolId, newClassroomId], [oldSchoolId, oldClassroomId]) => {
    if (newSchoolId !== oldSchoolId || newClassroomId !== oldClassroomId) {
      dashboardClassroomStore.reset()
      dataInitialized.value = false
      await loadClassroomData()
    }
  }
)
</script>

<template>
  <div class="dashboard-classroom">
    <!-- État de chargement -->
    <div v-if="localLoading" class="flex flex-col gap-4">
      <!-- Skeleton pour les métriques générales -->
      <p-skeleton height="10rem" class="mb-4" />

      <!-- Skeleton pour la liste de cours -->
      <p-skeleton height="20rem" class="mb-4" />

      <!-- Skeleton pour l'assiduité -->
      <p-skeleton height="15rem" />
    </div>

    <!-- État vide -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center p-8">
      <i class="pi pi-users text-surface-500 dark:text-surface-300 mb-4 text-4xl"></i>
      <h3 class="mt-0 mb-2">Données non disponibles</h3>
      <p class="text-surface-700 dark:text-surface-100 text-center">
        Les informations de ce groupe ne sont pas disponibles ou n'existent pas.
      </p>
    </div>

    <!-- Contenu de la classe -->
    <div v-else class="flex flex-col gap-8">
      <c-dashboard-classroom-general-metrics />

      <!-- Progression du groupe -->
      <c-dashboard-classroom-modularization
        :schoolId="props.schoolId"
        :classroom-id="props.classroomId"
      />

      <!-- Assiduité des élèves -->
      <c-dashboard-classroom-attendance
        :schoolId="props.schoolId"
        :classroom-id="props.classroomId"
      />
    </div>
  </div>
</template>
